/**
 * CaptureFormRenderer — self-contained renderer for a CaptureForm.
 *
 * Renders the form's header fields (one value each) plus, when the form has
 * line fields (target_kind='line'), a repeatable "Line Items" grid where each
 * row collects the line fields. Submits `{ values, lines }`:
 *   values — header answers keyed by field.key
 *   lines  — one object per row, line answers keyed by field.key
 *
 * Field type → control:
 *   single_select   → FormUtils/Select
 *   text / date     → FormUtils/TextInput
 *   number/currency → FormUtils/NumericInput
 *   notes           → FormUtils/TextareaInput
 *   product_variant → CaptureForms/fields/ProductVariantField
 *
 * No <form> element: submission is driven by the Submit button's onClick so the
 * renderer can be dropped inside any modal (some wrap their body in a <form>)
 * without nesting forms — nested forms cause stray submits/reloads.
 */
import { zodResolver } from '@hookform/resolvers/zod'
import { useMemo } from 'react'
import { Control, FieldValues, useFieldArray, useForm } from 'react-hook-form'
import { z } from 'zod'
import { PiPlus, PiTrash } from 'react-icons/pi'
import Select from 'components/FormUtils/Select'
import { TextInput } from 'components/FormUtils/TextInput'
import { NumericInput } from 'components/FormUtils/NumericInput'
import { TextareaInput } from 'components/FormUtils/TextareaInput'
import FbButton from 'components/FbUI/FbButton'
import { Ds2026ButtonProvider } from 'components/FbUI/Ds2026ButtonContext'
import type {
  CaptureFieldDefinition,
  CaptureForm,
} from '../../models/captureForms'
import { ProductVariantField } from './fields/ProductVariantField'

export interface CaptureSubmitValues {
  values: Record<string, unknown>
  lines: Record<string, unknown>[]
}

export interface CaptureFormRendererProps {
  form: CaptureForm
  onSubmit: (payload: CaptureSubmitValues) => void
  /** Prefill header field values (edit mode). Keyed by field.key. */
  initialValues?: Record<string, unknown>
  submitting?: boolean
  /** Read-only preview: render the fields but hide the submit + row controls. */
  preview?: boolean
}

function fieldSchema(field: CaptureFieldDefinition): z.ZodTypeAny {
  switch (field.field_type) {
    case 'number':
    case 'currency': {
      const base = z.number({
        invalid_type_error: `${field.label} is required`,
      })
      return field.required ? base : base.optional().nullable()
    }
    case 'date':
    case 'text':
    case 'notes': {
      const base = z.string()
      return field.required
        ? base.min(1, `${field.label} is required`)
        : base.optional().nullable()
    }
    case 'single_select':
      return field.required
        ? z.string().min(1, `${field.label} is required`)
        : z.string().optional().nullable()
    case 'product_variant': {
      const entity = z.object(
        { id: z.number(), label: z.string() },
        { invalid_type_error: `${field.label} is required` }
      )
      if (field.allow_multiple) {
        // Multi: an array of refs. Required means at least one selection.
        const arr = z.array(entity)
        return field.required ? arr.min(1, `${field.label} is required`) : arr
      }
      return field.required ? entity : entity.nullable().optional()
    }
    default:
      return z.unknown()
  }
}

// Field types whose empty value is null (numeric inputs) rather than an empty
// string. product_variant is handled explicitly in blankFor (its empty depends
// on allow_multiple: [] for multi, null for single).
const NULL_BLANK_TYPES: ReadonlySet<string> = new Set(['number', 'currency'])

function blankFor(field: CaptureFieldDefinition): unknown {
  if (field.field_type === 'product_variant') {
    return field.allow_multiple ? [] : null
  }
  return NULL_BLANK_TYPES.has(field.field_type) ? null : ''
}

function blankRow(
  lineFields: CaptureFieldDefinition[]
): Record<string, unknown> {
  return Object.fromEntries(lineFields.map((f) => [f.key, blankFor(f)]))
}

/** Render one field control at react-hook-form path `name`. */
function renderField(
  field: CaptureFieldDefinition,
  control: Control<FieldValues>,
  name: string
) {
  const label = field.required ? `${field.label} *` : field.label
  const rules = field.required
    ? { required: `${field.label} is required` }
    : undefined
  switch (field.field_type) {
    case 'single_select':
      return (
        <Select
          key={name}
          control={control}
          name={name}
          label={label}
          options={field.options}
          optional={!field.required}
          rules={rules}
        />
      )
    case 'text':
      return (
        <TextInput
          key={name}
          control={control}
          name={name}
          label={label}
          optional={!field.required}
          rules={rules}
        />
      )
    case 'number':
      return (
        <NumericInput
          key={name}
          control={control}
          fieldName={name}
          label={label}
          optional={!field.required}
        />
      )
    case 'currency':
      return (
        <NumericInput
          key={name}
          control={control}
          fieldName={name}
          label={label}
          optional={!field.required}
          prefix="$"
          decimalScale={2}
        />
      )
    case 'date':
      return (
        <TextInput
          key={name}
          control={control}
          name={name}
          label={label}
          type="date"
          optional={!field.required}
          rules={rules}
        />
      )
    case 'notes':
      return (
        <TextareaInput
          key={name}
          control={control}
          name={name}
          label={label}
          optional={!field.required}
          rules={rules}
        />
      )
    case 'product_variant':
      return (
        <ProductVariantField
          key={name}
          control={control}
          name={name}
          label={label}
          allowMultiple={field.allow_multiple}
        />
      )
    default:
      return null
  }
}

export function CaptureFormRenderer({
  form,
  onSubmit,
  submitting = false,
  initialValues,
  preview = false,
}: CaptureFormRendererProps) {
  const headerFields = useMemo(
    () =>
      form.fields
        .filter((f) => f.target_kind !== 'line')
        .sort((a, b) => a.order - b.order),
    [form]
  )
  const lineFields = useMemo(
    () =>
      form.fields
        .filter((f) => f.target_kind === 'line')
        .sort((a, b) => a.order - b.order),
    [form]
  )
  const hasLines = lineFields.length > 0

  const schema = useMemo(() => {
    const shape: Record<string, z.ZodTypeAny> = {}
    for (const f of headerFields) shape[f.key] = fieldSchema(f)
    if (hasLines) {
      const rowShape = Object.fromEntries(
        lineFields.map((f) => [f.key, fieldSchema(f)])
      )
      shape.lines = z.array(z.object(rowShape))
    }
    return z.object(shape)
  }, [headerFields, lineFields, hasLines])

  const { control, handleSubmit } = useForm<FieldValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      ...Object.fromEntries(
        headerFields.map((f) => [f.key, initialValues?.[f.key] ?? blankFor(f)])
      ),
      ...(hasLines ? { lines: [blankRow(lineFields)] } : {}),
    },
  })

  const {
    fields: rows,
    append,
    remove,
  } = useFieldArray({ control, name: 'lines' })

  function submit(values: FieldValues) {
    const { lines, ...headerValues } = values
    onSubmit({
      values: headerValues as Record<string, unknown>,
      lines: (lines as Record<string, unknown>[]) ?? [],
    })
  }

  return (
    <Ds2026ButtonProvider>
      <div>
        {headerFields.map((f) => renderField(f, control, f.key))}

        {hasLines && (
          <div className="mt-4">
            <div className="mb-2 flex items-center justify-between">
              <p className="text-sm font-semibold text-fg">Line Items</p>
              {!preview && (
                <button
                  type="button"
                  onClick={() => append(blankRow(lineFields))}
                  className="flex items-center gap-1 text-xs text-brand-active hover:text-brand"
                >
                  <PiPlus size={12} />
                  Add row
                </button>
              )}
            </div>
            {rows.map((row, i) => (
              <div
                key={row.id}
                className="mb-2 rounded-md border border-line bg-surface-sunken p-3"
              >
                <div className="mb-1 flex items-center justify-between">
                  <span className="text-xs font-semibold text-fg-secondary">
                    Row {i + 1}
                  </span>
                  {!preview && rows.length > 1 && (
                    <button
                      type="button"
                      onClick={() => remove(i)}
                      className="text-fg-tertiary hover:text-error"
                      aria-label="Remove row"
                    >
                      <PiTrash size={14} />
                    </button>
                  )}
                </div>
                {lineFields.map((f) =>
                  renderField(f, control, `lines.${i}.${f.key}`)
                )}
              </div>
            ))}
          </div>
        )}

        {!preview && (
          <div className="mt-4 flex justify-end">
            <FbButton
              type="button"
              variant="primary"
              disabled={submitting}
              onClick={handleSubmit(submit)}
            >
              {submitting ? 'Submitting…' : 'Submit'}
            </FbButton>
          </div>
        )}
      </div>
    </Ds2026ButtonProvider>
  )
}
